import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  orgDataApiRef,
  OrgDataApi,
  OrgDivision,
  OrgPlatform,
  OrgChapter,
  OrgDataApiResponse,
  CreateDivisionRequest,
  UpdateDivisionRequest,
  CreatePlatformRequest,
  UpdatePlatformRequest,
  CreateChapterRequest,
  UpdateChapterRequest,
} from '@internal/plugin-org-data-react';

// Local type aliases to avoid renaming every method signature in OrgDataClient.
type Division = OrgDivision;
type Platform = OrgPlatform;
type Chapter = OrgChapter;
type OrgDataResponse = OrgDataApiResponse;

// Re-export shared types and API ref so existing app-level imports keep working.
export type { OrgDataApi, OrgDataApiResponse as OrgDataResponse };
export type { OrgDivision as Division, OrgPlatform as Platform, OrgChapter as Chapter };
export type {
  CreateDivisionRequest,
  UpdateDivisionRequest,
  CreatePlatformRequest,
  UpdatePlatformRequest,
  CreateChapterRequest,
  UpdateChapterRequest,
};
export { orgDataApiRef };

/**
 * Org Data API client implementation
 */
export class OrgDataClient implements OrgDataApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('org-data');
  }

  private async fetch<T>(path: string, options?: RequestInit): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const errorField = errorBody.error;
      const errorMessage =
        typeof errorField === 'string'
          ? errorField
          : typeof errorField === 'object' && errorField?.message
            ? errorField.message
            : `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // Read operations
  async getOrgData(): Promise<OrgDataResponse> {
    return this.fetch<OrgDataResponse>('/org-data');
  }

  async getDivisions(): Promise<{ divisions: Division[] }> {
    return this.fetch<{ divisions: Division[] }>('/divisions');
  }

  async getDivision(id: string): Promise<Division> {
    return this.fetch<Division>(`/divisions/${encodeURIComponent(id)}`);
  }

  async getPlatforms(divisionId?: string): Promise<{ platforms: Platform[] }> {
    const query = divisionId ? `?divisionId=${encodeURIComponent(divisionId)}` : '';
    return this.fetch<{ platforms: Platform[] }>(`/platforms${query}`);
  }

  async getPlatform(id: string): Promise<Platform> {
    return this.fetch<Platform>(`/platforms/${encodeURIComponent(id)}`);
  }

  async getChapters(platformId?: string): Promise<{ chapters: Chapter[] }> {
    const query = platformId ? `?platformId=${encodeURIComponent(platformId)}` : '';
    return this.fetch<{ chapters: Chapter[] }>(`/chapters${query}`);
  }

  async getChapter(id: string): Promise<Chapter> {
    return this.fetch<Chapter>(`/chapters/${encodeURIComponent(id)}`);
  }

  // Write operations
  async createDivision(data: CreateDivisionRequest): Promise<Division> {
    return this.fetch<Division>('/divisions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDivision(id: string, data: UpdateDivisionRequest): Promise<Division> {
    return this.fetch<Division>(`/divisions/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteDivision(id: string): Promise<void> {
    return this.fetch<void>(`/divisions/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async createPlatform(data: CreatePlatformRequest): Promise<Platform> {
    return this.fetch<Platform>('/platforms', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePlatform(id: string, data: UpdatePlatformRequest): Promise<Platform> {
    return this.fetch<Platform>(`/platforms/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePlatform(id: string): Promise<void> {
    return this.fetch<void>(`/platforms/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  async createChapter(data: CreateChapterRequest): Promise<Chapter> {
    return this.fetch<Chapter>('/chapters', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateChapter(id: string, data: UpdateChapterRequest): Promise<Chapter> {
    return this.fetch<Chapter>(`/chapters/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteChapter(id: string): Promise<void> {
    return this.fetch<void>(`/chapters/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }
}
